import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactElement, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Permission, THREAD_TITLE_MAX, hasPermission, type Channel, type CommunityStructure, type ForumThread } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { cn } from "../../lib/cn";
import { fullDate, timeAgo } from "../../lib/format";
import { useInView, useIsWide } from "../../lib/hooks";
import { Avatar } from "../../components/ui/Avatar";
import { Button, IconButton } from "../../components/ui/Button";
import { EmptyState, ErrorState, Segmented } from "../../components/ui/Controls";
import { Dialog } from "../../components/ui/Dialog";
import { Field, Input, Textarea } from "../../components/ui/Input";
import { Skeleton } from "../../components/ui/Skeleton";
import { Spinner } from "../../components/ui/Spinner";
import { Tooltip } from "../../components/ui/Tooltip";
import { IconBack, IconComment, IconCompose, IconFire, IconLock, IconPaperclip, IconPinFilled, IconSortTime } from "../../components/icons";
import { UploadPreviews } from "../posts/Composer";
import { useUploads } from "../posts/useUploads";
import { PrivateMark, channelIcon } from "../communities/ChannelSidebar";
import { useThreads, threadKey, threadsKey, type ThreadSort } from "./hooks";
import { ThreadView } from "./ThreadView";
import { ThreadContextMenu } from "./ThreadMenu";

/** Wraps a post's button in its right-click menu. */
type MenuWrap = (child: ReactElement) => ReactNode;

type Tag = Channel["forumTags"][number];

/** Channel-level permission hints for posts. The server enforces these independently. */
export function forumPerms(channel: Channel, isOwner: boolean) {
  const has = (f: number) => isOwner || hasPermission(channel.permissions, f);
  return {
    create: has(Permission.CREATE_THREADS) && has(Permission.SEND_MESSAGES),
    manage: has(Permission.MANAGE_THREADS),
    attach: has(Permission.ATTACH_FILES),
  };
}

export function TagChip({ tag, active, onClick, size = "md" }: { tag: Tag; active?: boolean; onClick?: () => void; size?: "sm" | "md" }) {
  const cls = cn(
    "inline-flex shrink-0 items-center gap-1 rounded-full border whitespace-nowrap",
    size === "sm" ? "h-6 px-2 text-xs" : "h-7 px-2.5 text-sm",
    active ? "border-accent bg-accent-soft text-fg" : "border-line-strong text-fg-2",
    onClick && !active && "hover:bg-surface hover:text-fg",
  );
  const body = (
    <>
      {tag.emoji && <span aria-hidden>{tag.emoji}</span>}
      {tag.name}
    </>
  );
  return onClick ? (
    <button type="button" aria-pressed={!!active} onClick={onClick} className={cls}>
      {body}
    </button>
  ) : (
    <span className={cls}>{body}</span>
  );
}

function ThreadState({ thread }: { thread: ForumThread }) {
  return (
    <>
      {thread.pinned && (
        <Tooltip label="Pinned">
          <IconPinFilled className="icon size-4 shrink-0 text-fg-3" aria-label="Pinned" />
        </Tooltip>
      )}
      {thread.locked && (
        <Tooltip label="Locked">
          <IconLock className="icon size-4 shrink-0 text-fg-3" aria-label="Locked" />
        </Tooltip>
      )}
    </>
  );
}

function ThreadRow({ thread, tags, active, onOpen, menu }: { thread: ForumThread; tags: Map<string, Tag>; active: boolean; onOpen: () => void; menu: MenuWrap }) {
  return (
    <li>
      {menu(
        <button
          onClick={onOpen}
          aria-current={active ? "true" : undefined}
          className={cn("flex w-full gap-3 rounded-lg px-3 py-3 text-left transition-colors", active ? "bg-surface-2" : "hover:bg-surface")}
        >
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <ThreadState thread={thread} />
              <h3 className="truncate text-base font-semibold text-fg">{thread.title}</h3>
            </div>
            {thread.tagIds.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {[...tags.values()].filter((t) => thread.tagIds.includes(t.id)).map((t) => <TagChip key={t.id} tag={t} size="sm" />)}
              </div>
            )}
            {thread.preview && <p className="mt-1 line-clamp-2 text-sm text-fg-3">{thread.preview}</p>}
            <div className="mt-2 flex items-center gap-2 text-xs text-fg-3">
              {thread.author && <Avatar src={thread.author.avatarUrl} name={thread.author.displayName} size="xs" />}
              <span className="truncate">{thread.author?.displayName ?? "Deleted user"}</span>
              <span aria-hidden>·</span>
              <Tooltip label={`Last activity ${fullDate(thread.lastMessageAt)}`}>
                <time dateTime={thread.lastMessageAt} className="shrink-0 tabular">
                  {timeAgo(thread.lastMessageAt)}
                </time>
              </Tooltip>
              <span className="ml-auto flex shrink-0 items-center gap-1 tabular" aria-label={`${thread.replyCount} ${thread.replyCount === 1 ? "reply" : "replies"}`}>
                <IconComment className="icon size-4" aria-hidden />
                {thread.replyCount}
              </span>
            </div>
          </div>
          {thread.media?.kind === "image" && <img src={thread.media.url} alt={thread.media.alt ?? ""} className="size-16 shrink-0 rounded-md object-cover" loading="lazy" />}
        </button>,
      )}
    </li>
  );
}

/** Media channels: a gallery of posts by their first image or video. */
function ThreadTile({ thread, active, onOpen, menu }: { thread: ForumThread; active: boolean; onOpen: () => void; menu: MenuWrap }) {
  return (
    <li>
      {menu(
        <button onClick={onOpen} aria-current={active ? "true" : undefined} className={cn("group block w-full overflow-hidden rounded-lg text-left", active && "ring-2 ring-accent")}>
          <div className="relative aspect-square overflow-hidden bg-surface">
            {thread.media?.kind === "image" ? (
              <img src={thread.media.url} alt={thread.media.alt ?? ""} className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02] motion-reduce:transition-none motion-reduce:group-hover:scale-100" loading="lazy" />
            ) : thread.media?.kind === "video" ? (
              <video src={thread.media.url} className="h-full w-full object-cover" muted preload="metadata" />
            ) : null}
            <span className="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded-full bg-black/60 px-1.5 py-0.5 text-xs text-white tabular">
              <IconComment className="icon size-3.5" aria-hidden />
              {thread.replyCount}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-0.5 pt-1.5">
            <ThreadState thread={thread} />
            <span className="truncate text-sm font-medium text-fg">{thread.title}</span>
          </div>
        </button>,
      )}
    </li>
  );
}

/** Create a post, or edit an existing post's title and tags. */
export function PostDialog({
  open,
  onOpenChange,
  channel,
  communityId,
  thread,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  channel: Channel;
  communityId: string;
  thread?: ForumThread;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const editing = !!thread;
  const [title, setTitle] = useState("");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploads = useUploads({ max: 10, allowVideo: true });
  const media = channel.type === "media";

  useEffect(() => {
    if (!open) return;
    setTitle(thread?.title ?? "");
    setTagIds(thread?.tagIds ?? []);
    setBody("");
    setError(null);
    uploads.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, thread]);

  const toggleTag = (id: string) => setTagIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : cur.length >= 5 ? cur : [...cur, id]));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return setError("Give your post a title");
    if (!editing && !body.trim() && !uploads.mediaIds.length) return setError("Write something or attach a file");
    if (!editing && media && !uploads.mediaIds.length) return setError("Attach an image or video");
    setPending(true);
    setError(null);
    try {
      if (editing) {
        const updated = await api.patch<ForumThread>(`/api/threads/${thread!.id}`, { title, tagIds });
        qc.setQueryData(threadKey(updated.id), updated);
      } else {
        const created = await api.post<ForumThread>(`/api/channels/${channel.id}/threads`, { title, tagIds, content: body, mediaIds: uploads.mediaIds });
        qc.setQueryData(threadKey(created.id), created);
        navigate(`/c/${communityId}/${channel.id}/t/${created.id}`);
      }
      void qc.invalidateQueries({ queryKey: threadsKey(channel.id) });
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? "Edit post" : "New post"}
      size="lg"
      footer={
        <>
          {!editing && (
            <>
              <input ref={fileRef} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => e.target.files && uploads.add(e.target.files)} />
              <IconButton label="Attach files" icon={IconPaperclip} className="mr-auto" onClick={() => fileRef.current?.click()} />
            </>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="post-form" loading={pending} disabled={uploads.uploading}>
            {editing ? "Save" : "Post"}
          </Button>
        </>
      }
    >
      <form id="post-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Title" counter={`${THREAD_TITLE_MAX - title.length}`}>
          {(p) => <Input {...p} value={title} maxLength={THREAD_TITLE_MAX} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        {channel.forumTags.length > 0 && (
          <div role="group" aria-label="Tags" className="flex flex-wrap gap-1.5">
            {channel.forumTags.map((t) => (
              <TagChip key={t.id} tag={t} active={tagIds.includes(t.id)} onClick={() => toggleTag(t.id)} />
            ))}
          </div>
        )}
        {!editing && (
          <>
            <Field label="Message">
              {(p) => <Textarea {...p} rows={6} value={body} maxLength={2000} onChange={(e) => setBody(e.target.value)} placeholder={media ? "Add a caption" : "What would you like to discuss?"} />}
            </Field>
            <UploadPreviews items={uploads.items} onRemove={uploads.remove} />
          </>
        )}
        {error && (
          <p className="text-sm text-danger" role="alert">
            {error}
          </p>
        )}
      </form>
    </Dialog>
  );
}

export function ForumView({
  structure,
  channel,
  threadId,
  mobile,
}: {
  structure: CommunityStructure;
  channel: Channel;
  threadId?: string;
  mobile?: boolean;
}) {
  const navigate = useNavigate();
  const wide = useIsWide();
  const { community } = structure;
  const perms = forumPerms(channel, community.isOwner);
  const [sort, setSort] = useState<ThreadSort>("activity");
  const [tag, setTag] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const query = useThreads(channel.id, sort, tag);
  const threads = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);
  const tags = useMemo(() => new Map(channel.forumTags.map((t) => [t.id, t])), [channel.forumTags]);
  const sentinel = useInView<HTMLDivElement>(() => query.hasNextPage && !query.isFetchingNextPage && void query.fetchNextPage(), !!query.hasNextPage, "400px");
  const Icon = channelIcon(channel);
  const gallery = channel.type === "media";
  const base = `/c/${community.id}/${channel.id}`;
  const open = (id: string) => navigate(`${base}/t/${id}`);
  const menuFor =
    (t: ForumThread): MenuWrap =>
    (child) => (
      <ThreadContextMenu thread={t} channel={channel} communityId={community.id} isOwner={community.isOwner} onOpen={() => open(t.id)} onDeleted={() => t.id === threadId && navigate(base)}>
        {child}
      </ThreadContextMenu>
    );

  useEffect(() => setTag(null), [channel.id]);

  const thread = threadId ? <ThreadView key={threadId} structure={structure} channel={channel} threadId={threadId} onClose={() => navigate(base)} mobile={mobile} /> : null;
  // Narrow screens: the post replaces the list.
  if (thread && !wide) return thread;

  const list = (
    <div className={cn("flex h-full min-w-0 flex-col bg-bg", thread ? "w-[400px] shrink-0 border-r border-line" : "flex-1")}>
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line px-3">
        {mobile && <IconButton label="Back" icon={IconBack} tooltip={false} onClick={() => navigate(`/c/${community.id}`)} />}
        <Icon className="icon size-5 shrink-0 text-fg-3" aria-hidden />
        <h1 className="truncate text-base font-semibold text-fg">{channel.name}</h1>
        {channel.isPrivate && <PrivateMark className="text-fg-3" />}
        {channel.topic && !thread && (
          <>
            <span className="hidden h-5 w-px bg-line sm:block" />
            <Tooltip label={channel.topic}>
              <p className="hidden min-w-0 truncate text-sm text-fg-3 sm:block">{channel.topic}</p>
            </Tooltip>
          </>
        )}
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <Segmented
            ariaLabel="Sort posts"
            size="sm"
            value={sort}
            onChange={setSort}
            options={[
              { value: "activity", label: "Active", icon: IconFire },
              { value: "created", label: "New", icon: IconSortTime },
            ]}
          />
          {perms.create && (
            <Button size="sm" variant="primary" icon={IconCompose} onClick={() => setComposing(true)} aria-label={`New post in ${channel.name}`}>
              <span className={cn(thread ? "sr-only" : "hidden sm:inline")}>New post</span>
            </Button>
          )}
        </div>
      </header>

      {channel.forumTags.length > 0 && (
        <div role="group" aria-label="Filter by tag" className="no-scrollbar flex shrink-0 gap-1.5 overflow-x-auto border-b border-line px-3 py-2" data-swipe-ignore>
          {channel.forumTags.map((t) => (
            <TagChip key={t.id} tag={t} size="sm" active={tag === t.id} onClick={() => setTag(tag === t.id ? null : t.id)} />
          ))}
        </div>
      )}

      <div className="scroll-y min-h-0 flex-1 p-2">
        {query.isPending ? (
          <div className={gallery ? "grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 p-1" : "space-y-2 p-1"}>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className={gallery ? "aspect-square rounded-lg" : "h-20 rounded-lg"} />
            ))}
          </div>
        ) : query.isError ? (
          <ErrorState message={errorMessage(query.error, "Couldn't load posts")} onRetry={() => void query.refetch()} />
        ) : threads.length === 0 ? (
          <EmptyState
            icon={Icon}
            title={tag ? "No posts with this tag" : "No posts yet"}
            description={tag ? undefined : gallery ? "Share an image or video to start a conversation." : "Start a conversation with the first post."}
            action={
              perms.create && !tag ? (
                <Button variant="primary" icon={IconCompose} onClick={() => setComposing(true)}>
                  New post
                </Button>
              ) : undefined
            }
          />
        ) : gallery && !thread ? (
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 p-1">
            {threads.map((t) => (
              <ThreadTile key={t.id} thread={t} active={t.id === threadId} onOpen={() => open(t.id)} menu={menuFor(t)} />
            ))}
          </ul>
        ) : (
          <ul className="space-y-0.5">
            {threads.map((t) => (
              <ThreadRow key={t.id} thread={t} tags={tags} active={t.id === threadId} onOpen={() => open(t.id)} menu={menuFor(t)} />
            ))}
          </ul>
        )}
        <div ref={sentinel} />
        {query.isFetchingNextPage && (
          <div className="flex justify-center py-4 text-fg-3">
            <Spinner className="size-4" />
          </div>
        )}
      </div>
      <PostDialog open={composing} onOpenChange={setComposing} channel={channel} communityId={community.id} />
    </div>
  );

  return thread ? (
    <div className="flex h-full min-w-0 flex-1">
      {list}
      {thread}
    </div>
  ) : (
    list
  );
}
